// The one place that knows the session recorder is served under a different
// name than PostHog gives it. Both ends of the rename live here so they cannot
// drift: the browser rewrites the script URL before the tag is inserted, and
// the `/concierge` proxy rewrites it back before forwarding upstream.
//
// Why the rename exists: the `/concierge` proxy hides `i.posthog.com`, which
// defeats every host-anchored blocking rule, but EasyPrivacy also carries a
// bare path rule with no domain anchor:
//
//     /posthog-recorder.js
//
// That matches our own first-party URL, so remote config arrives, recording is
// enabled, and the recorder script alone is dropped - measured in production as
// sessions stuck at `lazy_loading` for as long as 43 minutes. Only the file
// name leaks; `/concierge/array/<token>/config.js` matches nothing, and the
// three `concierge` rules on the public lists are all anchored to unrelated
// third-party domains.

/** The name PostHog asks for, from the `scriptConfig.script` in remote config. */
const RECORDER_UPSTREAM_FILE = 'posthog-recorder.js';

/** What we serve it as. Checked against EasyPrivacy and EasyList: no match. */
const RECORDER_ALIAS_FILE = 'bellhop.js';

/** Swap the last segment of a path, leaving any prefix and query untouched. */
function renameLastSegment(pathname: string, from: string, to: string): string {
  const segments = pathname.split('/');
  if (segments[segments.length - 1] !== from) return pathname;
  segments[segments.length - 1] = to;
  return segments.join('/');
}

/**
 * Rewrite the recorder's script URL to the alias, for
 * `prepare_external_dependency_script`.
 *
 * Takes the absolute `src` the SDK already built and returns it with only the
 * file name changed, so the version query and any `strict_script_versioning`
 * path prefix survive. Any other external script - surveys, dead clicks,
 * toolbar - passes through untouched.
 */
export function aliasRecorderUrl(src: string): string {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    // Not a URL we can reason about; better to load the original than nothing.
    return src;
  }
  url.pathname = renameLastSegment(url.pathname, RECORDER_UPSTREAM_FILE, RECORDER_ALIAS_FILE);
  return url.toString();
}

/**
 * Undo the alias for the proxy, turning the path the browser asked for back
 * into the one PostHog's asset host actually serves. Every other path is
 * returned unchanged.
 */
export function resolveAliasedAssetPath(pathname: string): string {
  return renameLastSegment(pathname, RECORDER_ALIAS_FILE, RECORDER_UPSTREAM_FILE);
}
